import type {
  PullRequestMetadata,
  RunAggregateProjectionV2,
  WebTestRun
} from '@blume/web-test-model'
import { CircleCheck, CircleX, Eye, LoaderCircle, ServerCrash, type LucideIcon } from 'lucide-react'

import type { Tone } from '@/design/status'
import {
  aggregateProgress,
  groupPullRequestRuns,
  progressFromDurableAggregate,
  type AggregateProgress
} from '@/logic'

/** One revision of one pull request, joined to its durable verdict when live. */
export interface PullRequestGroup {
  key: string
  label: string
  sourceSha?: string
  repository?: string
  pullRequest?: number
  metadata?: PullRequestMetadata
  runs: WebTestRun[]
  progress: AggregateProgress
}

export function pullRequestGroups(
  runs: WebTestRun[],
  aggregates?: RunAggregateProjectionV2[]
): PullRequestGroup[] {
  if (aggregates) {
    const runsById = new Map(runs.map((run) => [run.id, run]))
    return aggregates.map((aggregate) => ({
      key: aggregate.key,
      label: aggregate.label,
      sourceSha: aggregate.sourceSha,
      repository: aggregate.repository,
      pullRequest: aggregate.pullRequest,
      metadata: aggregate.pullRequestMetadata,
      runs: aggregate.runIds.flatMap((id) => {
        const run = runsById.get(id)
        return run ? [run] : []
      }),
      progress: progressFromDurableAggregate(aggregate)
    }))
  }
  return groupPullRequestRuns(runs).map((group) => {
    const progress = aggregateProgress(group.runs)
    const latest = [...group.runs].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    )[0]
    return {
      ...group,
      ...(latest?.source.pullRequestMetadata
        ? { metadata: latest.source.pullRequestMetadata }
        : {}),
      progress
    }
  })
}

export function stalePullRequestKeys(groups: PullRequestGroup[]): Set<string> {
  const seenPullRequests = new Set<string>()
  const stale = new Set<string>()
  for (const group of groups) {
    if (!group.repository || !group.pullRequest) continue
    const identity = `${group.repository}:${group.pullRequest}`
    if (seenPullRequests.has(identity)) stale.add(group.key)
    else seenPullRequests.add(identity)
  }
  return stale
}

/** The merge answer, in words: the one thing the PR pages exist to say. */
export function mergeAnswer(progress: AggregateProgress): {
  label: string
  icon: LucideIcon
  tone: Tone
} {
  const counts = progress.counts
  const failures = counts.failed + counts['timed-out']
  // A known blocking verdict leads even while runs remain active — waiting
  // cannot make a failed revision safe. In-flight work is still mentioned,
  // as its own clause after the verdict.
  const inFlight =
    counts.active > 0
      ? ` · ${counts.active} run${counts.active === 1 ? '' : 's'} still in flight`
      : ''
  if (failures > 0) {
    return {
      label: `Not safe to merge — ${failures} failure${failures === 1 ? '' : 's'}${inFlight}`,
      icon: CircleX,
      tone: 'fail'
    }
  }
  if (counts['needs-review'] > 0) {
    return { label: `Review required before merge${inFlight}`, icon: Eye, tone: 'warn' }
  }
  if (counts.active > 0) {
    return {
      label: `Still verifying — ${counts.active} run${counts.active === 1 ? '' : 's'} in flight`,
      icon: LoaderCircle,
      tone: 'info'
    }
  }
  if (counts['infrastructure-error'] > 0) {
    return {
      label: 'No verdict — infrastructure failed, rerun the checks',
      icon: ServerCrash,
      tone: 'infra'
    }
  }
  if (counts.passed > 0) {
    return {
      label: `Safe to merge — all ${counts.passed} passed`,
      icon: CircleCheck,
      tone: 'pass'
    }
  }
  return { label: 'No completed verification', icon: Eye, tone: 'neutral' }
}

/** Hash-route href for one pull-request revision page. */
export function pullRequestHref(key: string): string {
  return `#/pull-requests/${encodeURIComponent(key)}`
}
